import React from "react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import NotificationMetadata from "@/components/notifications/NotificationMetadata";
import NotificationEditHistory from "@/components/notifications/NotificationEditHistory";
import NotificationDetailHeader from "@/components/notifications/NotificationDetailHeader";
import NotificationForm from "@/components/notifications/NotificationForm";
import NotificationChat from "@/components/notifications/NotificationChat";
import { Loader2 } from "lucide-react";
import { Notification } from "@/hooks/useNotificationDetail";
import { User } from "@/context/AuthContext";
import KlantInlichtenButton from "@/components/shared/KlantInlichtenButton";
import DetailEditActions from "@/components/notifications/NotificationActions";
import ArchiveRestoreActions from "@/components/notifications/ArchiveRestoreActions";
import DeleteNotificationButton from "@/components/notifications/DeleteNotificationButton";
import SendWebhookButton from "@/components/notifications/detail/SendWebhookButton";
import { NotificationActions } from "./NotificationActions";
import WebhookLogs from "./WebhookLogs";

interface NotificationDetailContainerProps {
  notification: Notification;
  editedNotification: Notification | null;
  editMode: boolean;
  setEditMode: (edit: boolean) => void;
  isAnalyzing: boolean;
  setIsAnalyzing: (analyzing: boolean) => void;
  showEditHistory: boolean;
  setShowEditHistory: (show: boolean) => void;
  editHistory: any[] | null;
  isLoadingHistory: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  handleAIAnalysisComplete: (data: { formattedText: string; voNumber: string | null }) => void;
  handleSubmit: (e: React.FormEvent) => void;
  resetNotification: () => void;
  updateNotification: any;
  canEdit: boolean;
  canDelete: boolean;
  onArchive: () => void;
  onRestore: () => void;
  onDelete: () => void;
  restorePending: boolean;
  archivePending: boolean;
  deletePending: boolean;
  user: User | null;
  onBack: () => void;
}

const NotificationDetailContainer = ({
  notification,
  editedNotification,
  editMode,
  setEditMode,
  isAnalyzing,
  setIsAnalyzing,
  showEditHistory,
  setShowEditHistory,
  editHistory,
  isLoadingHistory,
  handleInputChange,
  handleAIAnalysisComplete,
  handleSubmit,
  resetNotification,
  updateNotification,
  canEdit,
  canDelete,
  onArchive,
  onRestore,
  onDelete,
  restorePending,
  archivePending,
  deletePending,
  user,
  onBack
}: NotificationDetailContainerProps) => {
  // Modified handleEditMode function to always allow management users to edit
  const handleEditMode = (edit: boolean) => {
    // Management users can always edit, bypass permission check
    if (user?.role === "management") {
      console.log("Management user bypassing permission check for edit mode");
      setEditMode(edit);
      return;
    }
    
    // Other users still need proper permissions
    if (edit && !canEdit) {
      console.log("User lacks edit permissions, edit denied");
      return;
    }
    
    setEditMode(edit);
  };

  return (
    <Card>
      <CardHeader className="pb-3 px-3 sm:px-6">
        <NotificationDetailHeader
          isArchived={notification.archived}
          currentUser={user}
        />
        <div className="flex flex-wrap items-center gap-2 pt-2">
          {!editMode && (
            <>
            <SendWebhookButton
              notificationId={notification.id}
              disabled={notification.archived}
            />
            <KlantInlichtenButton
              orderid={notification.lightspeed_order_id ?? null}
              voNumber={notification.vo_number ?? notification.exact_order_number ?? null}
              customerNameHint={notification.customer_name ?? null}
            />
            </>
          )}
            <DetailEditActions
              editMode={editMode}
              showEditHistory={showEditHistory}
              setEditMode={handleEditMode}
              setShowEditHistory={setShowEditHistory}
              resetNotification={resetNotification}
              isPending={updateNotification.isPending}
              handleSubmit={handleSubmit}
            />
          {!editMode && (
            <>
              <ArchiveRestoreActions
                isArchived={notification.archived}
                onRestore={onRestore}
                onArchive={onArchive}
                restorePending={restorePending}
                archivePending={archivePending}
              />
              <DeleteNotificationButton
                onDelete={onDelete}
                isLoading={deletePending}
                isArchived={notification.archived}
              />
            </>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="grid gap-6 px-3 sm:px-6">
        <NotificationForm
          notification={notification}
          editMode={editMode}
          editedNotification={editedNotification}
          handleInputChange={handleInputChange}
          isAnalyzing={isAnalyzing}
          setIsAnalyzing={setIsAnalyzing}
          onAnalysisComplete={handleAIAnalysisComplete}
        />
        
        {!editMode && <NotificationChat notification={notification} />}
        
        {!editMode && user?.role === "management" && <WebhookLogs notificationId={notification.id} />}
        
        {showEditHistory && (
          <div className="pt-4 border-t">
            {isLoadingHistory ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <NotificationEditHistory 
                editHistory={editHistory} 
                currentUser={user}
              />
            )}
          </div>
        )}
      </CardContent>
      
      <CardFooter className="border-t px-3 sm:px-6 py-3 sm:py-4 flex flex-col gap-2 md:flex-row md:justify-between">
        <NotificationMetadata
          userName={notification.user_name}
          createdAt={notification.created_at}
          updatedAt={notification.updated_at}
        />
        
        <div className="flex justify-end gap-2">
          <NotificationActions onBack={onBack} />
        </div>
      </CardFooter>
    </Card>
  );
};

export default NotificationDetailContainer;
